import React, { useState } from 'react';
import {
  Card, CardContent, Typography, Grid, Box, Chip, Divider, Collapse, IconButton, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, TextField, CircularProgress,
  Tooltip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';

function MetricTile({ icon, label, value, bgColor, textColor }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        bgcolor: bgColor, borderRadius: 2, p: 2, flex: 1, minWidth: 140,
      }}
    >
      <Box sx={{ color: textColor, display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: textColor, lineHeight: 1.2 }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: textColor, opacity: 0.8 }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

function InfoRow({ label, value, sm = 6, md = 4 }) {
  return (
    <Grid item xs={12} sm={sm} md={md}>
      <Box sx={{ bgcolor: '#F8FAFC', borderRadius: 1.5, px: 2, py: 1.5, border: '1px solid #E3E8EF', height: '100%' }}>
        <Typography variant="subtitle2" sx={{ fontSize: '0.65rem', mb: 0.3 }}>
          {label}
        </Typography>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#0A1929' }}>
            {value}
          </Typography>
        ) : (
          value
        )}
      </Box>
    </Grid>
  );
}

const ACCESS_GROUP_LIMIT = 5;

export default function AppSummaryCard({
  summary,
  totalAuthEvents = 0,
  owners = [],
  onRemoveOwner,
  reviewers = [],
  reviewersLoading = false,
  selectedReviewer = null,
  onReviewerChange,
  onAddReviewer,
  addReviewerLoading = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [reviewerPopupOpen, setReviewerPopupOpen] = useState(false);

  const selectorFieldSx = {
    bgcolor: '#fff',
    borderRadius: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1A2027' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1A2027' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1A2027 !important',
        borderWidth: '1px',
      },
    },
  };

  const handleAddReviewerAndClose = async () => {
    if (onAddReviewer) {
      await onAddReviewer();
    }
    setReviewerPopupOpen(false);
  };

  if (!summary) return null;

  return (
    <Card sx={{ mb: 3 }}>
      {/* ── Always-visible header ──────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 3, py: 2, cursor: 'pointer', userSelect: 'none',
          '&:hover': { bgcolor: '#FAFBFC' },
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ color: '#0A1929', fontSize: '1rem' }}>
            {summary.appName}
          </Typography>
          {summary.isSignedOff ? (
            <Chip
              icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
              label="Signed Off"
              size="small"
              sx={{ bgcolor: '#E8F5E9', color: '#1B8A2D', fontWeight: 700, borderRadius: 1.5 }}
            />
          ) : (
            <Chip
              icon={<VerifiedUserOutlinedIcon sx={{ fontSize: 14 }} />}
              label="In Review"
              size="small"
              sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700, borderRadius: 1.5 }}
            />
          )}
<Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {summary.description}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Quick stats always visible */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#0D47A1', fontWeight: 600 }}>
              {summary.totalUsers} Users
            </Typography>
            <Typography variant="body2" sx={{ color: '#1B8A2D', fontWeight: 600 }}>
              {summary.retained} Retain
            </Typography>
            <Typography variant="body2" sx={{ color: '#C62828', fontWeight: 600 }}>
              {summary.revoked} Revoke
            </Typography>
            <Typography variant="body2" sx={{ color: '#E65100', fontWeight: 600 }}>
              {summary.pending} Pending
            </Typography>
          </Box>
          <IconButton size="small" sx={{ color: '#5A6A7A' }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* ── Collapsible content ────────────────────────────────────────────── */}
      <Collapse in={expanded}>
        <Divider />
        <CardContent sx={{ p: 3, pt: 2.5 }}>
          {/* Info row */}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ bgcolor: '#F8FAFC', borderRadius: 1.5, px: 2, py: 1.5, border: '1px solid #E3E8EF', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '0.65rem' }}>
                    Application Owner
                  </Typography>
                  <Tooltip title="Add Reviewer">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setReviewerPopupOpen(true); }}
                      sx={{
                        p: 0.4,
                        color: '#1976D2',
                        bgcolor: '#EBF5FF',
                        borderRadius: 1,
                        '&:hover': { bgcolor: '#BBDEFB' },
                      }}
                    >
                      <PersonAddAlt1OutlinedIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#0A1929' }}>
                  {summary.appOwner}
                </Typography>
                {(() => {
                  const additionalOwners = owners.filter(
                    (o) => o.role === 'App Owner' && o.email !== summary.appOwner
                  );
                  if (additionalOwners.length === 0) return null;
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                      {additionalOwners.map((owner) => (
                        <Chip
                          key={owner.userid}
                          label={owner.fullName}
                          size="small"
                          onDelete={() => onRemoveOwner && onRemoveOwner(owner.userid)}
                          sx={{
                            height: 22, fontSize: '0.68rem', fontWeight: 500,
                            bgcolor: '#EBF5FF', color: '#0D47A1', borderRadius: 1,
                            '& .MuiChip-deleteIcon': { fontSize: 14, color: '#1976D2', '&:hover': { color: '#C62828' } },
                          }}
                        />
                      ))}
                    </Box>
                  );
                })()}
              </Box>
            </Grid>
            <InfoRow
              label="Application Leader"
              value={summary.appLeader}
              sm={6} md={3}
            />
            <InfoRow
              label="Total Authentication Events"
              value={totalAuthEvents.toLocaleString()}
              sm={6} md={3}
            />
            <InfoRow
              label="Application Access Groups"
              sm={6} md={3}
              value={(() => {
                const groups = (summary.accessGroups || []).filter((g) => g && g.trim());
                const visible = showAllGroups ? groups : groups.slice(0, ACCESS_GROUP_LIMIT);
                const hasMore = groups.length > ACCESS_GROUP_LIMIT;
                return (
                  <Box sx={{ mt: 0.5 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {visible.map((group) => {
                        const isDirect = group.toUpperCase().startsWith('DIRECT_');
                        const displayLabel = isDirect ? 'DIRECT' : group;
                        return (
                          <Tooltip key={group} title={isDirect ? 'Direct user assignment' : ''} arrow placement="top" disableHoverListener={!isDirect}>
                            <Chip
                              label={displayLabel}
                              size="small"
                              sx={{
                                height: 22, fontSize: '0.68rem', fontWeight: 600,
                                bgcolor: '#EBF5FF',
                                color: '#0D47A1',
                                borderRadius: 1,
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>
                    {hasMore && (
                      <Button
                        size="small"
                        onClick={() => setShowAllGroups((prev) => !prev)}
                        sx={{ fontSize: '0.65rem', textTransform: 'none', mt: 0.5, p: 0, minWidth: 0, color: '#1976D2' }}
                      >
                        {showAllGroups ? 'Show Less' : `+${groups.length - ACCESS_GROUP_LIMIT} More`}
                      </Button>
                    )}
                  </Box>
                );
              })()}
            />
          </Grid>

          <Divider sx={{ mb: 2.5 }} />

          {/* Metric tiles */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
            <MetricTile
              icon={<PeopleAltOutlinedIcon />}
              label="Total Users"
              value={summary.totalUsers}
              bgColor="#EBF5FF"
              textColor="#0D47A1"
            />
            <MetricTile
              icon={<CheckCircleOutlineIcon />}
              label="Retain"
              value={summary.retained}
              bgColor="#E8F5E9"
              textColor="#1B8A2D"
            />
            <MetricTile
              icon={<HighlightOffIcon />}
              label="Revoke"
              value={summary.revoked}
              bgColor="#FFEBEE"
              textColor="#C62828"
            />
            <MetricTile
              icon={<HourglassBottomIcon />}
              label="Pending"
              value={summary.pending}
              bgColor="#FFF3E0"
              textColor="#E65100"
            />
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          {/* Notes */}
          <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E3E8EF', borderRadius: 2, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: '#5A6A7A' }} />
              <Typography variant="subtitle2" sx={{ fontSize: '0.7rem' }}>
                Important Notes
              </Typography>
            </Box>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="tbody">
                <Box component="tr">
                  <Box
                    component="td"
                    sx={{ py: 1, pr: 2, verticalAlign: 'top', whiteSpace: 'nowrap', borderBottom: '1px solid #E3E8EF' }}
                  >
                    <Chip
                      label="Retain items"
                      size="small"
                      sx={{ bgcolor: '#E8F5E9', color: '#1B8A2D', fontWeight: 700, fontSize: '0.7rem', borderRadius: 1 }}
                    />
                  </Box>
                  <Box
                    component="td"
                    sx={{ py: 1, verticalAlign: 'top', borderBottom: '1px solid #E3E8EF' }}
                  >
                    <Typography variant="body2" sx={{ color: '#1A2027' }}>
                      No action taken. Entitlement remains.
                    </Typography>
                  </Box>
                </Box>
                <Box component="tr">
                  <Box component="td" sx={{ py: 1, pr: 2, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    <Chip
                      label="Revoke items"
                      size="small"
                      sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 700, fontSize: '0.7rem', borderRadius: 1 }}
                    />
                  </Box>
                  <Box component="td" sx={{ py: 1, verticalAlign: 'top' }}>
                    <Typography variant="body2" sx={{ color: '#1A2027' }}>
                      IAM team will remove the access from the IAM system, if the access was assigned via URAM.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Collapse>
      {/* ── Add Reviewer Popup ─────────────────────────────────────────────── */}
      <Dialog
        open={reviewerPopupOpen}
        onClose={() => setReviewerPopupOpen(false)}
        onClick={(e) => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddAlt1OutlinedIcon sx={{ color: '#1976D2', fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, color: '#0A1929' }}>
            Add Reviewer
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Assign a reviewer for <strong>{summary.appName}</strong>. They will be able to review user access for this application.
          </Typography>
          <Autocomplete
            size="small"
            options={reviewers}
            value={selectedReviewer}
            onChange={(_, value) => onReviewerChange && onReviewerChange(value)}
            loading={reviewersLoading}
            disabled={reviewersLoading || addReviewerLoading}
            getOptionLabel={(option) => option?.name ? `${option.name} (${option.userId})` : option?.userId || ''}
            isOptionEqualToValue={(option, value) => option.userId === value.userId}
            noOptionsText="No reviewers available"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Reviewer"
                placeholder="Search by name or ID"
                sx={selectorFieldSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {reviewersLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setReviewerPopupOpen(false)}
            disabled={addReviewerLoading}
            sx={{ borderColor: '#1A2027', color: '#1A2027', textTransform: 'none', borderRadius: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={addReviewerLoading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PersonAddAlt1OutlinedIcon />}
            disabled={!selectedReviewer || addReviewerLoading}
            onClick={handleAddReviewerAndClose}
            sx={{
              bgcolor: '#0A1929',
              textTransform: 'none',
              borderRadius: 1,
              '&:hover': { bgcolor: '#1A3A5C' },
            }}
          >
            {addReviewerLoading ? 'Adding...' : 'Add Reviewer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
